import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Users, Cpu, Cloud, BarChart3, Settings, Phone, Briefcase } from 'lucide-react';
import { Button } from '../ui/button';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '../ui/navigation-menu';
import { cn } from '../../lib/utils';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_gita-enterprise/artifacts/18uc5tiv_Global%20IT%20-%20Logo.jpg";

const services = [
  {
    title: "IT Staffing & Talent Solutions",
    href: "/services/staffing",
    description: "Elite talent acquisition powered by AI-driven matching",
    icon: Users,
    primary: true
  },
  {
    title: "AI & Automation Solutions",
    href: "/services/ai-automation",
    description: "Transform operations with intelligent automation",
    icon: Cpu,
    primary: true
  },
  {
    title: "IT Consulting",
    href: "/services/consulting",
    description: "Strategic technology guidance for enterprise growth",
    icon: Settings
  },
  {
    title: "Cloud & DevOps",
    href: "/services/cloud-devops",
    description: "Modernize infrastructure and accelerate delivery",
    icon: Cloud
  },
  {
    title: "Data & Analytics",
    href: "/services/data-analytics",
    description: "Turn data into actionable business insights",
    icon: BarChart3
  }
];

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > 20);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <header
        className={cn(
          "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
          isScrolled 
            ? "bg-white/95 backdrop-blur-lg shadow-sm border-b border-slate-100" 
            : "bg-transparent"
        )}
        data-testid="main-navbar"
      >
        <div className="container-padding">
          <nav className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link 
              to="/" 
              className="flex items-center space-x-2"
              data-testid="nav-logo-link"
            >
              <img 
                src={LOGO_URL} 
                alt="Global IT Associates" 
                className="h-12 w-auto"
                loading="eager"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-1">
              <NavigationMenu>
                <NavigationMenuList>
                  <NavigationMenuItem>
                    <Link 
                      to="/"
                      className={cn(
                        "nav-link px-4 py-2",
                        isActive('/') && "text-[#2563EB]"
                      )}
                      data-testid="nav-home-link"
                    >
                      Home
                    </Link>
                  </NavigationMenuItem>

                  <NavigationMenuItem>
                    <NavigationMenuTrigger 
                      className="nav-link bg-transparent hover:bg-transparent"
                      data-testid="nav-services-trigger"
                    >
                      Services
                    </NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <ul className="grid w-[500px] gap-3 p-4 md:w-[600px] md:grid-cols-2 bg-white rounded-xl shadow-xl border border-slate-100">
                        {services.map((service) => (
                          <li key={service.title}>
                            <NavigationMenuLink asChild>
                              <Link
                                to={service.href}
                                className={cn(
                                  "block select-none rounded-lg p-3 leading-none no-underline outline-none transition-colors hover:bg-slate-50",
                                  service.primary && "bg-blue-50/50 hover:bg-blue-50"
                                )}
                                data-testid={`nav-service-${service.title.toLowerCase().replace(/\s+/g, '-')}`}
                              >
                                <div className="flex items-start space-x-3">
                                  <div className={cn(
                                    "w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0",
                                    service.primary ? "bg-[#2563EB]" : "bg-slate-100"
                                  )}>
                                    <service.icon className={cn(
                                      "w-5 h-5",
                                      service.primary ? "text-white" : "text-slate-600"
                                    )} />
                                  </div>
                                  <div>
                                    <div className="text-sm font-semibold text-slate-900">
                                      {service.title}
                                    </div>
                                    <p className="text-sm leading-snug text-slate-500 mt-1">
                                      {service.description}
                                    </p>
                                  </div>
                                </div>
                              </Link>
                            </NavigationMenuLink>
                          </li>
                        ))}
                      </ul>
                    </NavigationMenuContent>
                  </NavigationMenuItem>

                  <NavigationMenuItem>
                    <Link 
                      to="/about"
                      className={cn(
                        "nav-link px-4 py-2",
                        isActive('/about') && "text-[#2563EB]"
                      )}
                      data-testid="nav-about-link"
                    >
                      About
                    </Link>
                  </NavigationMenuItem>

                  <NavigationMenuItem>
                    <Link 
                      to="/case-studies"
                      className={cn(
                        "nav-link px-4 py-2",
                        isActive('/case-studies') && "text-[#2563EB]"
                      )}
                      data-testid="nav-case-studies-link"
                    >
                      Case Studies
                    </Link>
                  </NavigationMenuItem>

                  <NavigationMenuItem>
                    <Link 
                      to="/careers"
                      className={cn(
                        "nav-link px-4 py-2",
                        isActive('/careers') && "text-[#2563EB]"
                      )}
                      data-testid="nav-careers-link"
                    >
                      Careers
                    </Link>
                  </NavigationMenuItem>

                  <NavigationMenuItem>
                    <Link 
                      to="/contact"
                      className={cn(
                        "nav-link px-4 py-2",
                        isActive('/contact') && "text-[#2563EB]"
                      )}
                      data-testid="nav-contact-link"
                    >
                      Contact
                    </Link>
                  </NavigationMenuItem>
                </NavigationMenuList>
              </NavigationMenu>
            </div>

            {/* CTA Buttons */}
            <div className="hidden lg:flex items-center space-x-3">
              <Link to="/contact?type=hire_talent">
                <Button 
                  variant="outline" 
                  className="btn-outline text-sm"
                  data-testid="nav-hire-talent-btn"
                >
                  <Briefcase className="w-4 h-4 mr-2" />
                  Hire Talent
                </Button>
              </Link>
              <Link to="/contact">
                <Button 
                  className="btn-primary text-sm"
                  data-testid="nav-contact-btn"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Get Started
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6 text-slate-700" />
              ) : (
                <Menu className="w-6 h-6 text-slate-700" />
              )}
            </button>
          </nav>
        </div>
      </header>

      {/* Mobile Menu */}
      <div
        className={cn(
          "fixed inset-0 z-40 bg-white transform transition-transform duration-300 lg:hidden",
          isMobileMenuOpen ? "translate-x-0" : "translate-x-full"
        )}
        data-testid="mobile-menu"
      >
        <div className="pt-24 px-6 pb-8 h-full overflow-y-auto">
          <nav className="space-y-2">
            <Link 
              to="/"
              className="block py-3 text-lg font-medium text-slate-700 hover:text-[#2563EB] border-b border-slate-100"
              data-testid="mobile-nav-home"
            >
              Home
            </Link>
            
            <div className="py-3 border-b border-slate-100">
              <div className="text-lg font-medium text-slate-700 mb-3">Services</div>
              <div className="space-y-2 pl-4">
                {services.map((service) => (
                  <Link
                    key={service.title}
                    to={service.href}
                    className="block py-2 text-slate-600 hover:text-[#2563EB]"
                    data-testid={`mobile-nav-service-${service.title.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {service.title}
                  </Link>
                ))}
              </div>
            </div>

            <Link 
              to="/about"
              className="block py-3 text-lg font-medium text-slate-700 hover:text-[#2563EB] border-b border-slate-100"
              data-testid="mobile-nav-about"
            >
              About
            </Link>

            <Link 
              to="/case-studies"
              className="block py-3 text-lg font-medium text-slate-700 hover:text-[#2563EB] border-b border-slate-100"
              data-testid="mobile-nav-case-studies"
            >
              Case Studies
            </Link>

            <Link 
              to="/careers"
              className="block py-3 text-lg font-medium text-slate-700 hover:text-[#2563EB] border-b border-slate-100"
              data-testid="mobile-nav-careers"
            >
              Careers
            </Link>

            <Link 
              to="/contact"
              className="block py-3 text-lg font-medium text-slate-700 hover:text-[#2563EB] border-b border-slate-100"
              data-testid="mobile-nav-contact"
            >
              Contact
            </Link>
          </nav>

          <div className="mt-8 space-y-3">
            <Link to="/contact?type=hire_talent" className="block">
              <Button 
                variant="outline" 
                className="w-full btn-outline"
                data-testid="mobile-hire-talent-btn"
              >
                <Briefcase className="w-4 h-4 mr-2" />
                Hire Talent
              </Button>
            </Link>
            <Link to="/contact" className="block">
              <Button 
                className="w-full btn-primary"
                data-testid="mobile-contact-btn"
              >
                <Phone className="w-4 h-4 mr-2" />
                Get Started
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
